import { useState } from "react";
import { mutate } from "swr";
import { Loader2, Settings2, Trash2 } from "lucide-react";

import { useMembers } from "@/hooks/use-organization";
import { useOrganization } from "@/context/OrganizationContext";
import { OrganizationService, type OrgMember } from "@/services/organization.service";
import { ROLE_LABELS, type OrganizationRole } from "@/constants/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InviteMemberDialog } from "./invite-member-dialog";
import { MemberPermissionsDialog } from "./member-permissions-dialog";
import { RemoveMemberDialog } from "./remove-member-dialog";

export function MembersTable() {
  const { membership } = useOrganization();
  const { members, isLoading, isError } = useMembers();
  const [permissionsFor, setPermissionsFor] = useState<OrgMember | null>(null);
  const [removeFor, setRemoveFor] = useState<OrgMember | null>(null);
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const myRole = membership?.role;
  const canManage = myRole === "owner" || myRole === "admin";

  const canEditRole = (target: OrgMember): boolean => {
    if (!canManage || target.id === membership?.id) return false;
    if (target.role === "owner") return false; // no ownership transfer in v1
    if (target.role === "admin" && myRole !== "owner") return false;
    return true;
  };

  const canEditPermissions = (target: OrgMember): boolean => {
    if (!canManage || target.role === "owner") return false;
    if (target.role === "admin" && myRole !== "owner") return false;
    return true;
  };

  const canRemove = (target: OrgMember): boolean => {
    if (!canManage || target.id === membership?.id) return false;
    if (target.role === "owner") return false;
    if (target.role === "admin" && myRole !== "owner") return false;
    return true;
  };

  const handleRoleChange = async (target: OrgMember, role: string) => {
    setRoleUpdating(target.id);
    setError(null);
    try {
      await OrganizationService.updateMemberRole(target.id, role);
      mutate("organization-members");
    } catch (err: any) {
      setError(err?.message || "Failed to update the member role.");
    } finally {
      setRoleUpdating(null);
    }
  };

  if (isError) {
    return <div className="p-6 text-red-500">Failed to load members</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-6 pt-6">
        <p className="text-sm text-muted-foreground">
          People with access to this organization's workspace.
        </p>
        {canManage && <InviteMemberDialog />}
      </div>

      {error && <p className="px-6 text-sm text-red-500">{error}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-6">Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right pr-6">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : members.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                No members found.
              </TableCell>
            </TableRow>
          ) : (
            members.map((member) => (
              <TableRow key={member.id}>
                <TableCell className="pl-6">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {member.user.name || member.user.email}
                      {member.id === membership?.id && (
                        <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">{member.user.email}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {canEditRole(member) ? (
                    <Select
                      value={member.role}
                      onValueChange={(role) => handleRoleChange(member, role)}
                      disabled={roleUpdating === member.id}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {myRole === "owner" && <SelectItem value="owner">Owner</SelectItem>}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                      {ROLE_LABELS[member.role as OrganizationRole] ?? member.role}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {member.role !== "member" ? (
                    <span className="text-xs text-muted-foreground">All features</span>
                  ) : member.permissions && Object.keys(member.permissions).length > 0 ? (
                    <Badge variant="secondary">Custom</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">Role defaults</span>
                  )}
                </TableCell>
                <TableCell>{new Date(member.createdAt).toLocaleDateString()}</TableCell>
                <TableCell className="text-right pr-6">
                  <div className="flex items-center justify-end gap-1">
                    {canEditPermissions(member) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPermissionsFor(member)}
                        title="Feature permissions"
                      >
                        <Settings2 className="h-4 w-4" />
                        <span className="sr-only">Permissions</span>
                      </Button>
                    )}
                    {canRemove(member) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive/90"
                        onClick={() => setRemoveFor(member)}
                        title="Remove member"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove</span>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <MemberPermissionsDialog
        member={permissionsFor}
        open={permissionsFor !== null}
        onOpenChange={(open) => !open && setPermissionsFor(null)}
      />
      <RemoveMemberDialog
        member={removeFor}
        open={removeFor !== null}
        onOpenChange={(open) => !open && setRemoveFor(null)}
      />
    </div>
  );
}
